import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Bell, Volume2, VolumeX, ArrowLeft } from "lucide-react";

const POLL_MS = 7000;
const STORAGE_SEEN_KEY = "cph_admin_seen_buyer_ids";

const FIN_LABEL = {
  cash: "All Cash",
  hard_money: "Hard Money",
  dscr: "DSCR / Conv",
  mixed: "Mixed",
};
const REHAB_LABEL = {
  turnkey: "Turnkey",
  light: "Light",
  medium: "Medium",
  full_gut: "Full gut",
};
const VEL_LABEL = {
  "1": "1/mo",
  "2_4": "2-4/mo",
  "5_9": "5-9/mo",
  "10_plus": "10+/mo",
};

const CHIME_SRC =
  "data:audio/wav;base64,UklGRuQGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YcAGAAAAAAIAAwABAAIAAQACAAMAAQADAAEAAgACAAEAAQABAAEAAwACAAMAAwABAAMAAgACAAEAAwACAAIAAwADAAEAAQACAAMAAwACAAIAAwABAAEAAQADAAEAAwABAAIAAQADAAMAAwACAAIAAwABAAIAAwABAAEAAQACAAIAAwABAAEAAwACAAMAAgADAAEAAQACAAMAAwABAAMAAgADAAEAAQABAAMAAgACAAIAAwABAAEAAwACAAMAAQABAAIAAgABAAMAAwACAAIAAgACAAEAAwACAAIAAwABAAIAAwABAAIAAgACAAEAAQACAAIAAwADAAIAAwACAAEAAwACAAMAAwACAAMAAQABAAEAAgADAAEAAwADAAMAAgADAAIAAwACAAIAAgADAAMAAwABAAEAAgABAAEAAQABAAMAAgACAAIAAgADAAEAAwABAAIAAgABAAIAAwABAAMAAgADAAIAAgADAAMAAgADAAEAAgABAAEAAgACAAIAAwABAAEAAQACAAMAAwABAAEAAgACAAEAAQAAAAEAAwACAAIAAwACAAEAAgABAAMAAwADAAEAAgADAAEAAgACAAMAAgABAAEAAgACAAMAAwABAAIAAgACAAMAAwABAAEAAgACAAEAAgABAAEAAgABAAEAAgABAAMAAgABAAIAAwACAAMAAgADAAEAAgADAAIAAgACAAEAAQACAAIAAgABAAIAAwADAAEAAwACAAEAAQACAAEAAQADAAMAAQADAAIAAgADAAEAAgADAAIAAgACAAEAAQABAAMAAwACAAIAAwACAAIAAQADAAMAAwACAAMAAwADAAEAAwABAAEAAQABAAIAAQACAAIAAgACAAIAAgABAAMAAQACAAIAAgACAAEAAQADAAIAAgADAAIAAQABAAEAAwABAAEAAQACAAMAAQACAAMAAQADAAMAAwACAAEAAQACAAEAAwACAAMAAgABAAIAAQACAAEAAgACAAEAAwABAAMAAQADAAIAAwADAAEAAgACAAIAAgACAAEAAgADAAMAAQACAAIAAQACAAEAAQABAAMAAgABAAIAAwADAAMAAwACAAMAAwABAAIAAQABAAIAAgABAAEAAwABAAEAAQABAAIAAwABAAEAAQADAAIAAwABAAMAAQADAAEAAwABAAMAAwABAAMAAQACAAMAAgABAAMAAwADAAMAAwADAAEAAQABAAEAAQACAAIAAgACAAMAAgADAAMAAwACAAMAAwADAAEAAwACAAIAAQACAAIAAgABAAIAAQADAAEAAQACAAMAAgABAAIAAwACAAEAAwABAAIAAwACAAMAAgACAAMAAgADAAEAAwADAAEAAgADAAIAAQACAAEAAgACAAIAAwACAAMAAwACAAEAAwACAAIAAgAB";

export default function AdminCashBuyers() {
  const [buyers, setBuyers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [flashIds, setFlashIds] = useState(new Set());
  const [unread, setUnread] = useState(0);

  const seenRef = useRef(new Set());
  const audioRef = useRef(null);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_SEEN_KEY);
      if (raw) seenRef.current = new Set(JSON.parse(raw));
    } catch {}
  }, []);

  const persistSeen = () => {
    try {
      localStorage.setItem(
        STORAGE_SEEN_KEY,
        JSON.stringify(Array.from(seenRef.current))
      );
    } catch {}
  };

  const playChime = () => {
    if (!soundOn) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(CHIME_SRC);
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch {}
  };

  const notifyBrowser = (b) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      new Notification("New cash buyer — Clear Path Home Relief Services", {
        body: `${b.full_name} · ${b.email} · ${(b.asset_types || []).join(", ")}`,
        tag: b.id,
        icon: "/favicon.ico",
      });
    } catch {}
  };

  const askPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
    } catch {}
  };

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [b, s] = await Promise.all([
        api.get("/cash-buyers"),
        api.get("/cash-buyers/stats"),
      ]);
      const newBuyers = b.data;

      if (!firstLoadRef.current) {
        const fresh = newBuyers.filter((x) => !seenRef.current.has(x.id));
        if (fresh.length > 0) {
          playChime();
          fresh.slice(0, 3).forEach(notifyBrowser);
          setUnread((u) => u + fresh.length);
          setFlashIds((prev) => {
            const next = new Set(prev);
            fresh.forEach((f) => next.add(f.id));
            return next;
          });
          setTimeout(() => {
            setFlashIds((prev) => {
              const next = new Set(prev);
              fresh.forEach((f) => next.delete(f.id));
              return next;
            });
          }, 6000);
        }
      }
      newBuyers.forEach((x) => seenRef.current.add(x.id));
      persistSeen();
      firstLoadRef.current = false;

      setBuyers(newBuyers);
      setStats(s.data);
      setErr(null);
    } catch (e) {
      setErr(e?.message || "Failed to load buyers");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) New Buyers · Clear Path` : "Admin · Cash Buyers";
  }, [unread]);

  useEffect(() => {
    const onFocus = () => setUnread(0);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  return (
    <main data-testid="admin-cash-buyers-page" className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <Link
          to="/admin/leads"
          className="inline-flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0B1B3D] mb-4"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Leads
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#2C5545]">
              Clear Path Home Relief Services
            </div>
            <h1 className="font-display text-3xl lg:text-4xl text-[#0B1B3D]">
              Cash Buyer List
              {unread > 0 && (
                <span
                  data-testid="admin-buyer-unread-badge"
                  className="ml-3 inline-block align-middle text-sm bg-[#C5A059] text-[#0B1B3D] rounded-full px-3 py-1 font-sans font-semibold animate-pulse"
                >
                  {unread} new
                </span>
              )}
            </h1>
            <p className="text-xs text-[#64748B] mt-1">
              Live — auto-refreshes every {POLL_MS / 1000}s · Match deals to buyers
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundOn((v) => !v)}
              data-testid="admin-buyer-sound-toggle"
              className="px-3 py-2 text-sm rounded-md border border-[#0B1B3D]/30 text-[#0B1B3D] hover:bg-white flex items-center gap-2"
            >
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              {soundOn ? "Sound on" : "Sound off"}
            </button>

            {notifPerm !== "granted" ? (
              <button
                onClick={askPermission}
                className="px-3 py-2 text-sm rounded-md bg-[#C5A059] text-[#0B1B3D] hover:bg-[#B38F4E] font-medium flex items-center gap-2"
              >
                <Bell className="h-4 w-4" />
                Enable alerts
              </button>
            ) : (
              <span className="px-3 py-2 text-sm rounded-md bg-[#2C5545]/10 text-[#2C5545] flex items-center gap-2">
                <Bell className="h-4 w-4" /> Alerts on
              </span>
            )}

            <button
              onClick={() => load(false)}
              className="px-4 py-2 text-sm rounded-md border border-[#0B1B3D] text-[#0B1B3D] hover:bg-white"
            >
              Refresh
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard k={stats.total} v="Total Buyers" />
            <StatCard k={stats.cash} v="All Cash" />
            <StatCard k={stats.hard_money} v="Hard Money" />
            <StatCard k={stats.dscr} v="DSCR" />
            <StatCard k={stats.mixed} v="Mixed" />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-[#64748B]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading buyers...
          </div>
        ) : err ? (
          <div className="text-red-700">{err}</div>
        ) : buyers.length === 0 ? (
          <div className="rounded-lg border border-[#E5E3DB] bg-white p-10 text-center text-[#64748B]">
            No cash buyers yet. Buyer signups appear here in real time.
          </div>
        ) : (
          <div className="rounded-lg border border-[#E5E3DB] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-buyers-table">
              <thead className="bg-[#F3F2EE] text-[#0B1B3D] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Name / Company</th>
                  <th className="text-left p-3">Contact</th>
                  <th className="text-left p-3">Markets</th>
                  <th className="text-left p-3">Asset Types</th>
                  <th className="text-left p-3">Price Range</th>
                  <th className="text-left p-3">Buy Box</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((b) => {
                  const isNew = flashIds.has(b.id);
                  return (
                    <tr
                      key={b.id}
                      data-testid={`admin-buyer-row-${b.id}`}
                      className={`border-t border-[#E5E3DB] transition-colors ${
                        isNew ? "bg-[#C5A059]/15" : ""
                      }`}
                    >
                      <td className="p-3 whitespace-nowrap text-[#64748B]">
                        {isNew && (
                          <span className="inline-block mr-2 text-[10px] uppercase tracking-wider bg-[#C5A059] text-[#0B1B3D] rounded-full px-2 py-0.5 font-semibold">
                            New
                          </span>
                        )}
                        {new Date(b.created_at).toLocaleString()}
                      </td>
                      <td className="p-3 text-[#0B1B3D]">
                        <div className="font-medium">{b.full_name}</div>
                        {b.company && (
                          <div className="text-xs text-[#64748B]">{b.company}</div>
                        )}
                      </td>
                      <td className="p-3">
                        <a href={`tel:${b.phone}`} className="block text-[#0B1B3D] hover:text-[#C5A059]">
                          {b.phone}
                        </a>
                        <a href={`mailto:${b.email}`} className="block text-xs text-[#64748B] hover:text-[#0B1B3D]">
                          {b.email}
                        </a>
                      </td>
                      <td className="p-3 text-[#64748B]">{b.target_markets || "—"}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(b.asset_types || []).map((a) => (
                            <span
                              key={a}
                              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0B1B3D]/5 text-[#0B1B3D]"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-[#0B1B3D] whitespace-nowrap">
                        {b.price_min || "—"} – {b.price_max || "—"}
                      </td>
                      <td className="p-3 text-[#64748B] text-xs">
                        {[
                          FIN_LABEL[b.financing_type] && `Fin: ${FIN_LABEL[b.financing_type]}`,
                          REHAB_LABEL[b.rehab_tolerance] && `Rehab: ${REHAB_LABEL[b.rehab_tolerance]}`,
                          VEL_LABEL[b.deals_per_month] && `Vol: ${VEL_LABEL[b.deals_per_month]}`,
                          b.message && `"${b.message}"`,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}

function StatCard({ k, v }) {
  return (
    <div className="rounded-lg bg-white border border-[#E5E3DB] p-5">
      <div className="font-display text-3xl text-[#0B1B3D] leading-none">{k}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#64748B] mt-2">{v}</div>
    </div>
  );
}
